/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

/**
 * Shared Time-of-Use (TOU) cost ranking and color tier definitions.
 *
 * The Figma design exposes only four blue shades for the TOU bar; the six
 * known TOU names are bucketed into those four cost tiers. Anything not in
 * the known list defaults to tier 1 (cheapest, lightest).
 *
 * The hex values map to the Figma `--blue-100/400/600/800` design tokens.
 * The project's MUI theme does not yet define a full blue scale, so we
 * declare them here and re-use them anywhere the bar / tooltip needs to
 * agree on a tier color.
 */

export const TOU_TIER_OFF_PEAK = 1;
export const TOU_TIER_PARTIAL_PEAK = 2;
export const TOU_TIER_WINTER_ON_PEAK = 3;
export const TOU_TIER_SUMMER_ON_PEAK = 4;

export type TouTier = 1 | 2 | 3 | 4;

export const TOU_TIER_COLORS: Record<TouTier, string> = {
  1: "#EBF5F7",
  2: "#8DC8D9",
  3: "#006681",
  4: "#00334E",
};

export const TOU_TRACK_COLOR = "#F5F5F5";
export const TOU_SEGMENT_BORDER = "rgba(0, 0, 0, 0.12)";

export const normalizeTouName = (name?: string | null): string =>
  (name ?? "").trim();

/**
 * Cost-tier resolver tolerant of TOU naming variants returned by the API.
 *
 * The Genability/Arcadia feed has been observed to use any of these spellings:
 *   "Winter Off Peak"   "Winter Off-Peak"
 *   "Winter Part Peak"  "Winter Partial Peak"
 *   "Winter Peak"       "Winter On-Peak"   "Winter On Peak"
 *   ...same for Summer...
 *
 * Rather than enumerate every combination, we lower-case the name and check
 * for the season + type tokens. Anything unrecognized falls back to the
 * cheapest (lightest) tier per spec.
 */
export const getTouTier = (name?: string | null): TouTier => {
  const lower = normalizeTouName(name).toLowerCase();
  if (!lower) return TOU_TIER_OFF_PEAK;

  const isSummer = lower.includes("summer");
  const isWinter = lower.includes("winter");

  if (lower.includes("off")) {
    return TOU_TIER_OFF_PEAK;
  }
  if (lower.includes("partial") || lower.includes("part")) {
    return TOU_TIER_PARTIAL_PEAK;
  }
  if (lower.includes("peak")) {
    if (isSummer) return TOU_TIER_SUMMER_ON_PEAK;
    if (isWinter) return TOU_TIER_WINTER_ON_PEAK;
    return TOU_TIER_WINTER_ON_PEAK;
  }
  return TOU_TIER_OFF_PEAK;
};

export const getTouTierColor = (name?: string | null): string =>
  TOU_TIER_COLORS[getTouTier(name)];
